// OpenTelemetry OTLP HTTP exporter for traces
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'
import axios from 'axios'

import { isCUI } from '@/consts/routes'
import {
  apiClient,
  apiClientChatting,
  apiClientV2,
  apiClientV3,
} from '@/utils/axios'

import { OTEL_SERVICE_NAME, OTEL_SERVICE_VERSION, OTEL_URL } from './consts'
import {
  otelErrorInterceptor,
  otelRequestInterceptor,
  otelResponseInterceptor,
} from './interceptors'

// List of axios clients to attach OpenTelemetry interceptors to
const clientsToIntercept = [
  axios,
  apiClient,
  apiClientV2,
  apiClientV3,
  apiClientChatting,
]

export function initializeOpenTelemetry(tracingEnabled: boolean) {
  // Only enable tracing in CUI (Chatting UI) context
  if (isCUI && import.meta.env.PROD) {
    // Resource describes this service for tracing
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: OTEL_SERVICE_NAME,
      [ATTR_SERVICE_VERSION]: OTEL_SERVICE_VERSION,
    })

    // OTLP exporter config: sends traces to Jaeger backend
    const exporter = new OTLPTraceExporter({
      headers: {
        Authorization:
          'Basic ' +
          btoa(window.__OTEL_USERNAME__ + ':' + window.__OTEL_PASSWORD__),
      },
      url: OTEL_URL,
    })

    // WebTracerProvider: main entry for browser tracing
    const provider = new WebTracerProvider({
      resource,
      sampler: new ParentBasedSampler({
        // 100% sampling rate for tracing enabled, 1% for tracing disabled
        root: new TraceIdRatioBasedSampler(tracingEnabled ? 1 : 0.01),
      }),
      spanProcessors: [
        new BatchSpanProcessor(exporter, {
          maxExportBatchSize: 30,
        }),
      ],
    })

    provider.register()

    // Attach OpenTelemetry interceptors to all axios clients
    clientsToIntercept.forEach((client) => {
      client.interceptors.request.use(otelRequestInterceptor)
      client.interceptors.response.use(
        otelResponseInterceptor,
        otelErrorInterceptor,
      )
    })
  }
}
